import React, {useContext, useEffect, useState} from "react";
import axios from "axios";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowRightFromBracket,
    faChartColumn,
    faChevronLeft,
    faChevronRight,
    faCubes,
    faFileLines,
    faGauge,
    faGear,
    faLocationDot,
    faUser,
    faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {useLocation, useNavigate} from "react-router-dom";
import {ApiDataContext} from "./ApiDataContext";
import * as Constants from "../utils/Constants";
import {getAuthToken, getLoggedInUserId} from "../utils/utility";
import {resetMobiTrackStore} from "../utils/mobiTrackRealtime";
import {hasPermission, isSuperAdmin, getRole, ROLES} from "../utils/rolePermissions";
import BottomNav from "../components/layout/BottomNav";
import packageJson from "../../package.json";
import {disableWebPush} from "../utils/webPush";

/* ======================================================
   LOGOUT FUNCTION
====================================================== */
export const logoutFun = async () => {
  try {
    const token = getAuthToken();
    axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${token}`;
    await axios.get(Constants.BASE_URL + "logout");
  } catch (err) {
    console.error("Logout error:", err);
  } finally {
    resetMobiTrackStore(); // don't let one user's Track Point presence leak into the next session
    localStorage.clear();
    window.location.href = "/";
  }
};

/**
 * A person choosing to sign out: also stops vehicle alerts in this browser (utils/webPush).
 * Session expiry uses plain logoutFun instead, so alerts keep arriving after it — same rule as
 * the Android app.
 */
export const explicitLogout = async () => {
  await disableWebPush();
  return logoutFun();
};

/* ======================================================
   SIDEBAR COMPONENT
====================================================== */
export default function HomeSideNavBar() {
  const [selectedItem, setSelectedItem] = useState("Dashboard");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { fetchData } = useContext(ApiDataContext);
  const location = useLocation();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin();

  /* ======================================================
     SIDEBAR SECTION VISIBILITY — a section header ("Manage", "Insights", ...)
     must not render when every item underneath it is permission-gated away.
     Track Point (MOBI_TRACK) and Client Employee both have neither Assets nor
     Users access, so "Manage" rendered as a bare, item-less label; Employee
     and Track Point also both lack Reports/Audit Log, leaving "Insights"
     empty too — a driver saw two section titles stacked back-to-back with
     nothing under either. Each flag mirrors exactly the condition(s) gating
     that section's MenuItems below, so it can't drift out of sync with them.
  ====================================================== */
  const showOperateSection = hasPermission("sidebar_dashboard") || hasPermission("sidebar_tracking");
  const showManageSection = hasPermission("sidebar_assets") || hasPermission("sidebar_users");
  const showInsightsSection =
    hasPermission("sidebar_reports") || (superAdmin && hasPermission("sidebar_auditLog"));

  /* ======================================================
     FETCH DEVICES (CLIENT USERS ONLY)
  ====================================================== */
  const prepareAndFetchAllDevices = () => {
    if (superAdmin) return;
    const user = JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER));
    if (!user?.clientId) return;
    const isEmployee = getRole() === ROLES.CLIENT_EMPLOYEE;
    const userParam = isEmployee ? `?userId=${getLoggedInUserId()}` : "";
    const endPoint = `${Constants.API_V1}/${user.clientId}/${Constants.EP_ASSETS_GET_ALL}${userParam}`;
    fetchData(endPoint);
  };

  /* ======================================================
     SIDEBAR TOGGLE
  ====================================================== */
  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
    document.body.classList.toggle("sidebar-collapsed");
  };

  /* ======================================================
     NAVIGATION HANDLER
  ====================================================== */
  const handleNavigation = (path, itemName) => {
    setSelectedItem(itemName);
    navigate(path);
    setIsMobileOpen(false);
    document.body.classList.remove("mobile-sidebar-open");
    if (itemName === "Dashboard") prepareAndFetchAllDevices();
  };

  /* ======================================================
     SYNC MENU WITH ROUTE
  ====================================================== */
  useEffect(() => {
    const path = location.pathname;
    if (path === "/home")                      setSelectedItem("Dashboard");
    else if (path.includes("/tracking"))       setSelectedItem(Constants.TRACKING);
    else if (path.includes("/assets"))         setSelectedItem(Constants.ASSETS);
    else if (path.includes("/users"))          setSelectedItem(Constants.USERS);
    else if (path.includes("/tasks"))          setSelectedItem(Constants.TASK);
    else if (path.includes("/trips"))          setSelectedItem(Constants.TRIPS);
    else if (path.includes("/reports"))        setSelectedItem(Constants.REPORTS);
    else if (path.includes("/settings"))       setSelectedItem(Constants.SETTINGS);
    else if (path.includes("/profile"))        setSelectedItem(Constants.PROFILE);
    else if (path.includes("/command-center")) setSelectedItem(Constants.COMMAND_CENTER);
    else if (path.includes("/audit-log"))      setSelectedItem(Constants.AUDIT_LOG);
  }, [location.pathname]);

  /* ======================================================
     FETCH WHEN DASHBOARD IS ACTIVE
  ====================================================== */
  useEffect(() => {
    if (selectedItem === "Dashboard") prepareAndFetchAllDevices();
  }, [selectedItem]);

  /* ======================================================
     FETCH ON PAGE REFRESH (/home)
  ====================================================== */
  useEffect(() => {
    if (location.pathname === "/home") prepareAndFetchAllDevices();
  }, [location.pathname]);

  /* ======================================================
     BODY CLASS SYNC - Desktop collapsed
  ====================================================== */
  useEffect(() => {
    document.body.classList.toggle("sidebar-collapsed", isCollapsed);
    return () => document.body.classList.remove("sidebar-collapsed");
  }, [isCollapsed]);

  /* ======================================================
     BODY CLASS SYNC - Mobile open state
  ====================================================== */
  useEffect(() => {
    const checkMobileState = () => {
      setIsMobileOpen(document.body.classList.contains("mobile-sidebar-open"));
    };
    const observer = new MutationObserver(checkMobileState);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  /* ======================================================
     CLOSE MOBILE SIDEBAR ON OVERLAY CLICK
  ====================================================== */
  const handleOverlayClick = () => {
    setIsMobileOpen(false);
    document.body.classList.remove("mobile-sidebar-open");
  };

  /* ======================================================
     MENU ITEM
  ====================================================== */
  const MenuItem = ({ name, icon, path, label }) => (
    <li
      className={[
        selectedItem === name ? Constants.SELECTED : "",
        name === "Logout" ? "logout-item" : "",
      ].filter(Boolean).join(" ")}
      onClick={() =>
        name === "Logout" ? explicitLogout() : handleNavigation(path, name)
      }
      title={isCollapsed ? (label || name) : ""}
    >
      <div className="icon-container">
        <FontAwesomeIcon icon={icon} fixedWidth />
      </div>
      <span className="nav-text">{label || name}</span>
    </li>
  );

  /* ======================================================
     RENDER
  ====================================================== */
  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      <div
        className={[
          "col-vertical-menu",
          isCollapsed ? "collapsed" : "",
          isMobileOpen ? "mobile-open" : "",
        ].filter(Boolean).join(" ")}
      >
        <ul className="menu-list">
          {/* Toggle button at top */}
          <div className="toggle-btn-container">
            <button className="sidebar-toggle-btn" onClick={toggleSidebar} title="Toggle sidebar">
              <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
            </button>
          </div>

          {/* OPERATE — strict primary nav (MoM point 8): Tracking, Command
              Center, Tasks and Trips moved out of the sidebar — they are
              reached via the dashboard's per-device-type Quick Actions row
              (DeviceTypeActions). Routes stay registered in App.js. */}
          {showOperateSection && <li className="nav-section">Operate</li>}
          {/* Track Point (MOBI_TRACK) has no Dashboard access (see rolePermissions.js) — it
              only ever sees Live Tracking, so that's its one Operate destination instead. Every
              other role keeps Dashboard as the entry point and reaches Tracking via the
              dashboard's per-device-type Quick Actions row, as before. */}
          {hasPermission("sidebar_dashboard")
            ? <MenuItem name="Dashboard" icon={faGauge} path="/home" />
            : hasPermission("sidebar_tracking") && <MenuItem name="Live Tracking" icon={faLocationDot} path="/tracking" />}

          {/* MANAGE — entity management */}
          {showManageSection && <li className="nav-section">Manage</li>}
          {hasPermission("sidebar_assets")        && <MenuItem name={Constants.ASSETS}         icon={faCubes}          path="/assets" />}
          {hasPermission("sidebar_users")         && <MenuItem name={Constants.USERS}          icon={faUsers}          path="/users" />}

          {/* INSIGHTS — reports + audit */}
          {showInsightsSection && <li className="nav-section">Insights</li>}
          {hasPermission("sidebar_reports")       && <MenuItem name={Constants.REPORTS}        icon={faChartColumn}    path="/reports" />}
          {superAdmin && hasPermission("sidebar_auditLog") && <MenuItem name={Constants.AUDIT_LOG} icon={faFileLines} path="/audit-log" />}

          <div className="spacer" />

          {/* PERSONAL */}
          {hasPermission("sidebar_settings")      && <MenuItem name={Constants.SETTINGS}       icon={faGear}           path="/settings" />}
          {hasPermission("sidebar_profile")       && <MenuItem name={Constants.PROFILE}        icon={faUser}           path="/profile" />}
          <MenuItem name="Logout" icon={faArrowRightFromBracket} path="#" />

          <div className="sidebar-version" title={`v${packageJson.version}`}>
            <span className="sidebar-version-text">v{packageJson.version}</span>
          </div>
        </ul>
      </div>

      <BottomNav
        selectedItem={selectedItem}
        onNavigate={handleNavigation}
        hasPermission={hasPermission}
      />
    </>
  );
}
